import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../components/utils/Copyable';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import {
  GetUsitcChapter99MappingsBIZcoreTD,
  WipeUsitcChapter99MappingsBIZcoreTD,
  FetchUsitcChapter99MappingsBIZcoreTD
} from '../../../../../redux/actions/business/core/tradeDefencesActions';

import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import UsitcChapter99MappingsModal from './UsitcChapter99MappingsModal';

import global from "../../../../../global.scss";

const UsitcChapter99MappingsPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.tradeDefence"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [mappingsList, setMappingsList] = useState([]);
  const [mappingsTotal, setMappingsTotal] = useState(0);

  const [wipeConfirmOpen, setWipeConfirmOpen] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [fetchConfirmOpen, setFetchConfirmOpen] = useState(false);
  const [fetching, setFetching] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    { label: "Id", sort: "id" },
    { label: "Revision id", sort: "ref_revision_id" },
    { label: "Chapter 99 HTS", sort: "chapter_99_hts" },
    { label: "Base HTS start", sort: "target_base_hts_start" },
    { label: "Base HTS end", sort: "target_base_hts_end" },
    { label: "Remedy type", sort: "remedy_type" },
    { label: "Effective date", sort: "effective_date" },
    { label: "Expiration date", sort: "expiration_date" },
    { label: "Time stamps", sort: "created_at" },
    { label: "", sort: null },
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetMappings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      setMappingsList(state.usitcChapter99MappingsList.records || []);
      setMappingsTotal(state.usitcChapter99MappingsList.total || 0);
    } catch (err) {}
  }, [state]);

  async function handleGetMappings(customLoading) {
    let query = "";
    if (search) query += `&search=${search}`;
    if (sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    dispatch(GetUsitcChapter99MappingsBIZcoreTD(page + 1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  };

  const handleWipe = () => {
    setWipeConfirmOpen(false);
    dispatch(WipeUsitcChapter99MappingsBIZcoreTD(setWiping)).then(() => {
      handleGetMappings();
    });
  };

  const handleFetch = () => {
    setFetchConfirmOpen(false);
    dispatch(FetchUsitcChapter99MappingsBIZcoreTD(setFetching)).then(() => {
      handleGetMappings();
    });
  };

  return (
    <Box>

      <UsitcChapter99MappingsModal
        open={modalOpen}
        setOpen={setModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetMappings}
      />

      <ConfirmationModal
        open={wipeConfirmOpen}
        setOpen={setWipeConfirmOpen}
        title={"Wipe Chapter 99 Mappings Table"}
        onCancel={() => setWipeConfirmOpen(false)}
        onConfirm={handleWipe}
      />

      <ConfirmationModal
        open={fetchConfirmOpen}
        setOpen={setFetchConfirmOpen}
        title={"Fetch Chapter 99 Mappings from USITC"}
        onCancel={() => setFetchConfirmOpen(false)}
        onConfirm={handleFetch}
      />

      <TopBar
        title={"USITC Chapter 99 Mappings"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        button3Text={wiping ? "Wiping..." : "Wipe table"}
        button3OnClick={() => setWipeConfirmOpen(true)}
        button2Text={fetching ? "Fetching..." : "Fetch data"}
        button2OnClick={() => setFetchConfirmOpen(true)}
        button1Text={"Add mapping"}
        button1OnClick={() => {
          setUpdate({ defaultMode: 1 });
          setModalTitle("Add Chapter 99 mapping");
          setModalOpen(!modalOpen);
        }}
      />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          mappingsList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No Chapter 99 mapping found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort}
                        direction={sortDirection}
                        onClick={() => handleSort(heading.sort)}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                mappingsList.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>
                        <Copyable>{row.id || <NA />}</Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>
                        <Copyable>{row.ref_revision_id || <NA />}</Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.chapter_99_hts || <NA />}</TableCell>
                    <TableCell>{row.target_base_hts_start || <NA />}</TableCell>
                    <TableCell>{row.target_base_hts_end || <NA />}</TableCell>
                    <TableCell>{row.remedy_type || <NA />}</TableCell>
                    <TableCell>{row.effective_date || <NA />}</TableCell>
                    <TableCell>{row.expiration_date || <NA />}</TableCell>
                    <TableCell>
                      <TableTimeStamps createdAt={row.created_at} />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget);
                          setMenuOpen(true);
                          setUpdate(row);
                          setModalTitle(row.chapter_99_hts || row.id || "");
                        }}>
                        <MoreVertIcon sx={{ color: global['secondary_light'] }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={10}
                  count={mappingsTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
      }

      <Menu
        open={menuOpen}
        anchorEl={menuAnchor}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit Chapter 99 mapping");
            setUpdate({ ...update, defaultMode: 2 });
            setMenuOpen(false);
            setModalOpen(!modalOpen);
          }}>
          <Box className="flexCenter_Row" gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
      </Menu>

    </Box>
  );
};

export default UsitcChapter99MappingsPage;
